import axios from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import i18n from './i18n';

import { Platform } from '@/services/CustomerService';
import { Product } from '@/services/ProductsService';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalize(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function capitalizeWords(str: string) {
  return str
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

export function priceFormatter(price: number) {
  const formattedNumber = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);

  return formattedNumber;
}

export function formatPriceByPlatform(price: number, platform: Platform) {
  if (platform === Platform.VTEX) {
    return price / 100;
  }

  return price;
}

export function priceFormatterV2(
  price: number,
  locale: string,
  platform: Platform,
): string {
  const formattedPriceByPlatform = formatPriceByPlatform(price, platform);

  const localeCurrencyMap: Record<string, string> = {
    'PT-BR': 'BRL',
    'ES-CO': 'COP',
    'EN-US': 'USD',
  };

  const currency = localeCurrencyMap[locale.toUpperCase()] || 'BRL';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(formattedPriceByPlatform);
}

export function priceFormatterByLocale(price: number, locale: string) {
  const localeCurrencyMap: Record<string, string> = {
    'PT-BR': 'BRL',
    'ES-CO': 'COP',
    'EN-US': 'USD',
  };

  const currency = localeCurrencyMap[locale.toUpperCase()] || 'BRL';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(price);
}

export function formatSubmissionDate(submissionDate: Date | null | undefined) {
  if (!submissionDate) {
    return 'N/A';
  }

  const dateObject = new Date(submissionDate.toString().replace(' ', 'T'));
  const formattedDate = dateObject.toLocaleDateString('pt-BR');
  const formattedTime = dateObject.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${formattedDate} ${formattedTime}`;
}

export function convertToFileList(files: File[]): FileList {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
}

export async function convertFilesToBase64(files: FileList): Promise<string[]> {
  return Promise.all(
    Array.from(files).map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
      });
    }),
  );
}

export function delay(ms: number = 1000): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getAxiosErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      'An unexpected error occurred.'
    );
  }

  return error;
}

export function mountProductTags(product: Product) {
  const tags = [];

  if (product.shopTheLook) {
    tags.push(i18n.t('pages.VisionProduct.components.PairsWith.tag'));
  }

  // if (!product.stock || !product.isActive) {
  //   tags.push(i18n.t('pages.VisionProduct.components.UnavailableProduct.tag'));
  // }

  return tags;
}

export function randomInt(min: number = 1000, max: number = 9999) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function getFormattedUsername(
  name?: string,
  email?: string,
  userProfileId?: string,
) {
  const displayName = name ? name : (email ?? userProfileId);
  const initial = displayName?.[0]?.toUpperCase() ?? '';

  return {
    initial,
    displayName,
  };
}
